import { api } from './api';

export type StudentStatus = 'active' | 'at-risk' | 'inactive';
export type SubscriptionStatus = 'ACTIVE' | 'CANCELED' | 'PAST_DUE' | 'TRIALING' | null;

export interface AdminOverview {
  totalStudents: number;
  activeStudents: number;
  atRiskStudents: number;
  avgTrainingProgress: number;
  avgInterviewScore: number | null;
  avgCvScore: number | null;
  subscriptionBreakdown: Array<{ status: SubscriptionStatus; count: number }>;
}

export interface StudentSummary {
  id: string;
  email: string;
  name: string | null;
  subscriptionStatus: SubscriptionStatus;
  createdAt: string;
  trainingProgressPct: number;
  algoSolved: number;
  interviewCount: number;
  avgInterviewScore: number | null;
  cvScore: number | null;
  lastActiveAt: string | null;
  status: StudentStatus;
  platformStatus: 'PENDING' | 'ACTIVE';
}

export interface StudentsPage {
  data: StudentSummary[];
  total: number;
  page: number;
  limit: number;
}

export interface InterviewSummary {
  id: string;
  title: string;
  score: number | null;
  strengths: string[];
  improvements: string[];
  summary: string;
  createdAt: string;
}

export interface StudentDetail {
  profile: {
    id: string;
    email: string;
    name: string | null;
    interests: string[];
    learningGoals: string | null;
    createdAt: string;
    subscriptionStatus: SubscriptionStatus;
    status: StudentStatus;
    platformStatus: 'PENDING' | 'ACTIVE';
    onboardingCompleted: boolean;
    onboardingData: Record<string, unknown> | null;
    lastActiveAt: string | null;
  };
  kpis: {
    trainingProgressPct: number;
    algoSolved: number;
    interviewCount: number;
    avgInterviewScore: number | null;
    cvScore: number | null;
  };
  training: {
    completedLessons: number;
    totalLessons: number;
    progressPct: number;
    recentCompletions: Array<{ lessonId: string; lessonTitle: string; completedAt: string | null }>;
  };
  algorithms: {
    solved: number;
    attempted: number;
    notStarted: number;
    recentSubmissions: Array<{
      problemTitle: string;
      problemSlug: string;
      difficulty: 'EASY' | 'MEDIUM' | 'HARD';
      status: string;
      language: string;
      createdAt: string;
    }>;
  };
  jobs: Array<{
    id: string;
    company: string;
    role: string;
    status: string;
    appliedAt: string;
    interviews: InterviewSummary[];
  }>;
  sessions: Array<{
    id: string;
    title: string;
    status: string;
    scheduledStart: string;
    tutorName: string | null;
    notes: string | null;
  }>;
  cv: {
    resumeScore: number | null;
    linkedinScore: number | null;
    resumeSuggestions: string[];
    resumeRedFlags: string[];
    resumeVerdict: string | null;
    linkedinSuggestions: string[];
    linkedinRedFlags: string[];
  };
  interviewTimeline: Array<{ date: string; score: number }>;
}

export interface DailyActivityEntry {
  date: string;
  lessons: number;
  algo: number;
  interviews: number;
  chat: number;
  total: number;
}

export interface StudentActivitySummary {
  id: string;
  name: string | null;
  email: string;
  total: number;
  dailySeries: Array<{ date: string; total: number }>;
}

export interface StudentListParams {
  search?: string;
  status?: StudentStatus | '';
  fromDate?: string;
  toDate?: string;
  page?: number;
  limit?: number;
}

export const adminService = {
  getOverview: () => api.get<AdminOverview>('/admin/overview'),

  listStudents: (params: StudentListParams = {}) => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.status) qs.set('status', params.status);
    if (params.fromDate) qs.set('fromDate', params.fromDate);
    if (params.toDate) qs.set('toDate', params.toDate);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return api.get<StudentsPage>(`/admin/students${query ? `?${query}` : ''}`);
  },

  getStudentDetail: (id: string) => api.get<StudentDetail>(`/admin/students/${id}`),

  getPlatformActivity: (days: number) =>
    api.get<DailyActivityEntry[]>(`/admin/activity?days=${days}`),

  getStudentsActivity: (days: number) =>
    api.get<StudentActivitySummary[]>(`/admin/activity/by-student?days=${days}`),

  getStudentActivity: (id: string, days: number) =>
    api.get<DailyActivityEntry[]>(`/admin/students/${id}/activity?days=${days}`),

  updateUserStatus: (id: string, status: 'PENDING' | 'ACTIVE') =>
    api.patch(`/admin/users/${id}/status`, { status }),
};
